import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Home,
  Receipt,
  Calculator,
  ShieldCheck,
  Percent,
  ClipboardCheck,
  Clock,
  Wallet,
  ExternalLink,
  Tag,
  Plus,
} from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";
import ShareBar from "../components/landing/ShareBar";

/**
 * /rentals — dedicated landing page for the rental-property investor
 * campaign. Purpose: a shareable link for Facebook / LinkedIn that funnels
 * rental investors into signing the IRD Authority to Act form on the
 * NZ Taxation Ltd website.
 *
 * Primary CTA:   /rentals/signup (signed IRD authority form)
 * Free tool:     https://clientops.nz/tools/wealth-plan (Wealth Plan)
 *
 * Suggested Facebook post copy (paste this on the NZ Taxation FB page):
 * --------------------------------------------------------------------
 * Own a rental in New Zealand?
 *
 * Rental tax rules keep changing — interest deductibility, brightline,
 * ring-fencing, chattels depreciation. Miss one and it costs you.
 *
 * At NZ Taxation Limited we prepare your annual rental tax return
 * (IR3R + IR3) so you don't have to think about it.
 *
 * Sign up online in five minutes — sign the IRD Authority to Act and
 * we'll take it from there:
 *   https://nztaxation.nz/rentals/signup
 *
 * And before you sign, try our free Wealth Plan tool to see what your
 * rental is really doing for your long-term wealth:
 *   https://clientops.nz/tools/wealth-plan
 * --------------------------------------------------------------------
 */
export default function RentalsPage() {
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/rentals`
      : "https://nztaxation.nz/rentals";

  const shareTitle = "Rental tax compliance done for you — NZ Taxation Limited";
  const shareDescription =
    "Own a rental in New Zealand? We handle your annual IR3R, interest deductibility, chattels depreciation, brightline and ring-fencing calculations. Sign the IRD Authority to Act and we take it from there.";

  const WEALTH_URL = "https://clientops.nz/tools/wealth-plan";
  const SIGNUP_URL = "/rentals/signup";

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#0F172A]"
      data-testid="rentals-page"
    >
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-14 md:pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="rentals-back"
          >
            <ArrowLeft className="w-3 h-3" /> nztaxation.nz
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/70 backdrop-blur"
          >
            <Home className="w-3 h-3 text-[#C9A349]" />
            For rental property investors
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.98]"
            data-testid="rentals-headline"
          >
            Own a rental? <br />
            <span className="text-[#C9A349]">
              We&apos;ll handle the tax paperwork.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed"
          >
            Rental tax rules in New Zealand keep changing — interest
            deductibility, brightline, ring-fencing, chattels depreciation.
            Miss one and it costs you real money. NZ Taxation Limited prepares
            your <strong>annual rental tax return (IR3R + IR3)</strong> so you
            don&apos;t have to think about it.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link
              to={SIGNUP_URL}
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="rentals-enrol-cta-hero"
            >
              Sign the authority form <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={WEALTH_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-[#C9A349]/60 bg-[#C9A349]/10 text-[#0A192F] px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] transition-colors"
              data-testid="rentals-wealth-cta-hero"
            >
              Try free Wealth Plan tool <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> IRD registered tax agent
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> One rental or a portfolio — same care
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> Fully remote — Auckland based
            </span>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            What&apos;s included
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            Everything the IRD expects — <span className="text-[#C9A349]">handled for you.</span>
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <IncludedCard
              icon={Receipt}
              title="IR3R rental schedule + IR3 return"
              body="We prepare and file your annual rental income schedule alongside your personal tax return — one job, one accountant."
            />
            <IncludedCard
              icon={Percent}
              title="Interest deductibility calc"
              body="Correctly apportioned interest under the current phasing rules — including new-builds where the full deduction still applies."
            />
            <IncludedCard
              icon={Calculator}
              title="Chattels depreciation schedule"
              body="Carpets, curtains, appliances, heat pumps — we set up and maintain the depreciation register so you claim every dollar you're entitled to."
            />
            <IncludedCard
              icon={ShieldCheck}
              title="Brightline test check"
              body="Buying, selling or transferring? We check whether the brightline applies before it becomes a nasty surprise at tax time."
            />
            <IncludedCard
              icon={ClipboardCheck}
              title="Ring-fenced losses tracker"
              body="Rental losses ring-fence to your rental portfolio. We track carried-forward losses year on year so nothing gets lost."
            />
            <IncludedCard
              icon={Home}
              title="Mixed-use, boarders, Airbnb"
              body="Short-stay, mixed-use, boarder income, holiday homes — we know the specific rules for each and apply them correctly."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="rentals-pricing" className="py-20 md:py-28 bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Transparent pricing
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            One flat annual fee — <span className="text-[#C9A349]">no surprises.</span>
          </h2>
          <p className="mt-5 text-slate-600 max-w-2xl text-base md:text-lg leading-relaxed">
            The same clear price every year. You pay when we file — never on a per-email meter.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {/* Base fee */}
            <div className="bg-white border-2 border-[#0A192F] p-8 md:p-10 relative" data-testid="rentals-price-base">
              <div className="absolute top-0 right-0 bg-[#C9A349] text-[#0A192F] px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">
                Base fee
              </div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">
                <Tag className="w-3 h-3 text-[#C9A349]" /> Per rental property, per year
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-heading font-black text-5xl md:text-6xl text-[#0A192F] tracking-tight tabular-nums">
                  $499
                </span>
                <span className="text-lg font-heading font-bold text-slate-500">+ GST</span>
              </div>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Covers <b>one rental property</b> and <b>one IR3 personal tax return</b>. Includes
                everything on the &ldquo;What&apos;s included&rdquo; list — IR3R, interest deductibility,
                chattels depreciation, brightline check and ring-fenced losses.
              </p>
            </div>

            {/* Add-on for extra owners */}
            <div className="bg-[#0A192F] text-white p-8 md:p-10 relative" data-testid="rentals-price-extra">
              <div className="absolute top-0 right-0 bg-white text-[#0A192F] px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">
                Extra owner
              </div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
                <Plus className="w-3 h-3" /> Per additional IR3
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-heading font-black text-5xl md:text-6xl text-white tracking-tight tabular-nums">
                  +$100
                </span>
                <span className="text-lg font-heading font-bold text-slate-400">+ GST</span>
              </div>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Most rentals are co-owned. Add <b>$100 + GST</b> per additional owner&apos;s IR3
                return — so a property owned 50/50 by a couple is <b>$599 + GST all-in</b>.
              </p>
            </div>
          </div>

          <div className="mt-8 border-l-4 border-[#C9A349] bg-white px-5 md:px-6 py-4 text-slate-600 text-sm md:text-[15px] leading-relaxed max-w-3xl">
            <b className="text-[#0A192F]">Worked example.</b> One rental, owned equally by two spouses ={" "}
            <span className="font-heading font-bold text-[#0A192F]">$499 + $100 = $599 + GST per year</span>. Two rentals owned by
            the same couple = <span className="font-heading font-bold text-[#0A192F]">2 × $599 = $1,198 + GST per year</span>.
          </div>
        </div>
      </section>

      {/* Wealth Plan feature block */}
      <section className="py-20 md:py-28 bg-[#0A192F] text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute top-0 -right-40 w-[36rem] h-[36rem] bg-[#C9A349]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#C9A349]/40 bg-[#C9A349]/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
              <Wallet className="w-3 h-3" /> Free tool · Before you enrol
            </div>
            <h2 className="mt-6 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight">
              See what your rental is really <br />
              <span className="text-[#C9A349]">doing for your wealth.</span>
            </h2>
            <p className="mt-5 text-slate-300 text-base md:text-lg max-w-xl leading-relaxed">
              The <strong className="text-white">ClientOps Wealth Plan</strong> is a free planning
              tool built by NZ Taxation Limited. Enter your property details and see a
              plain-English picture of yield, cash flow, tax position and long-term wealth
              trajectory — before you commit to anything.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={WEALTH_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-white transition-colors"
                data-testid="rentals-wealth-cta"
              >
                Open the Wealth Plan tool <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to={SIGNUP_URL}
                className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-white/10 transition-colors"
                data-testid="rentals-enrol-cta-mid"
              >
                Or sign the authority form <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative bg-white/5 border border-white/10 backdrop-blur p-6 md:p-8">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
                Sample output
              </div>
              <div className="mt-3 font-heading font-black text-2xl">
                18 Manaia Rd, Titirangi
              </div>
              <dl className="mt-5 space-y-3 text-sm">
                <MetricRow label="Gross yield" value="4.9%" />
                <MetricRow label="Net yield (after tax)" value="2.1%" />
                <MetricRow label="Cash-flow position" value="-$142 / week" tone="warn" />
                <MetricRow label="10-year equity growth" value="+$284k" tone="good" />
                <MetricRow label="Deductible interest %" value="80%" />
              </dl>
              <div className="mt-5 pt-4 border-t border-white/10 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Preview — actual output uses your live figures
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why us — 3 quick benefits */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Why NZ Taxation Limited
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            A specialist for landlords — <span className="text-[#C9A349]">not a generalist.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <BenefitCard
              icon={Clock}
              title="$499 + GST per property"
              body="Flat annual fee per rental, plus $100 + GST for each additional owner's IR3. No surprise bills, no per-email meter."
            />
            <BenefitCard
              icon={ShieldCheck}
              title="IRD-registered tax agents"
              body="We hold the extension of time with IRD — you get more time to file and pay, at no extra charge."
            />
            <BenefitCard
              icon={Home}
              title="ClientOps included"
              body="Every rental client gets access to ClientOps — dashboards, docs upload, and your accountant one message away."
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Common questions
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight">
            Rental tax, <span className="text-[#C9A349]">answered.</span>
          </h2>
          <div className="mt-10 space-y-6">
            <FaqRow
              q="Do I need to file a return if my rental made a loss?"
              a="Yes — losses are ring-fenced to your rental portfolio and carried forward to offset future rental income. Filing every year keeps that carry-forward alive."
            />
            <FaqRow
              q="How much interest can I actually deduct in the current tax year?"
              a="It depends on whether the property is a new-build (100% deductible) or an existing dwelling (currently 80%, moving back to 100%). We apply the correct rules to your specific loan structure."
            />
            <FaqRow
              q="What if I have one rental and my day job is on PAYE?"
              a="We combine your IR3R (rental) with your personal IR3 return in one job. You get one fixed fee, one point of contact, one tax outcome."
            />
            <FaqRow
              q="I own the rental in a trust or LTC — can you still help?"
              a="Yes. We work with individuals, LTCs, look-through companies, trusts and partnerships. Sign up on the authority form and we'll match you to the right structure."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0A192F] text-white bg-grid-dark">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
            Ready to hand it over?
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-4xl mx-auto">
            Sign the authority form <br />
            <span className="text-[#C9A349]">and stop worrying about rental tax.</span>
          </h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto text-lg">
            Five minutes to sign. We take it from there — every rule, every deadline, every year.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to={SIGNUP_URL}
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-8 py-5 font-heading font-bold uppercase tracking-[0.14em] text-base hover:bg-white transition-colors"
              data-testid="rentals-enrol-cta-final"
            >
              Sign the authority form <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={WEALTH_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-5 font-heading font-bold uppercase tracking-[0.14em] text-base hover:bg-white/10 transition-colors"
              data-testid="rentals-wealth-cta-final"
            >
              Try Wealth Plan first <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <ShareBar url={url} title={shareTitle} description={shareDescription} />

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

function IncludedCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-slate-200 bg-white p-7 flex gap-5 items-start hover:-translate-y-1 transition-transform">
      <div className="w-11 h-11 flex items-center justify-center bg-[#0A192F] text-[#C9A349] flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="font-heading font-black text-lg md:text-xl text-[#0A192F] tracking-tight">
          {title}
        </h3>
        <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function BenefitCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-slate-200 bg-white p-8">
      <Icon className="w-6 h-6 text-[#C9A349]" />
      <h3 className="mt-5 font-heading font-black text-xl md:text-2xl text-[#0A192F] tracking-tight">
        {title}
      </h3>
      <p className="mt-3 text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function MetricRow({ label, value, tone }) {
  const toneClass =
    tone === "good"
      ? "text-emerald-400"
      : tone === "warn"
      ? "text-amber-300"
      : "text-white";
  return (
    <div className="flex items-baseline justify-between border-b border-white/5 pb-2">
      <span className="text-slate-400 text-xs uppercase tracking-[0.18em]">{label}</span>
      <span className={`font-heading font-black tabular-nums ${toneClass}`}>{value}</span>
    </div>
  );
}

function FaqRow({ q, a }) {
  return (
    <div className="border-l-2 border-[#C9A349] bg-white px-5 md:px-6 py-5">
      <div className="font-heading font-black text-lg text-[#0A192F]">{q}</div>
      <p className="mt-2 text-slate-600 leading-relaxed">{a}</p>
    </div>
  );
}
